import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  Button,
  Card,
  Empty,
  Input,
  Select,
  Spin,
  Tag,
  Typography,
} from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { getInstitution } from "../../../api/institutionApi";
import { getAccountsForAdmin } from "../../../api/bankAccountApi";
import InstitutionAccountsTable from "../../../components/accounts/InstitutionAccountsTable";
import { headInstitutionAccountColumns } from "../../../components/accounts/HeadInstitutionAccountColumns";
import { getTypeColor } from "../../../utils/themeUtils";

const { Title, Text } = Typography;

const STATUS_OPTIONS = [
  { label: "Идэвхтэй", value: "ACTIVE" },
  { label: "Идэвхгүй", value: "INACTIVE" },
];

function HeadInstitutionAccountsPage() {
  const { institutionId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [institution, setInstitution] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);

  const [institutionLoading, setInstitutionLoading] = useState(true);
  const [accountsLoading, setAccountsLoading] = useState(true);

  const [institutionError, setInstitutionError] = useState("");
  const [accountsError, setAccountsError] = useState("");

  const [searchText, setSearchText] = useState(searchParams.get("search") || "");
  const [selectedBanks, setSelectedBanks] = useState(
    searchParams.get("banks") ? searchParams.get("banks").split(",") : []
  );
  const [selectedCurrencies, setSelectedCurrencies] = useState(
    searchParams.get("currencies")
      ? searchParams.get("currencies").split(",")
      : []
  );
  const [selectedStatuses, setSelectedStatuses] = useState(
    searchParams.get("statuses") ? searchParams.get("statuses").split(",") : []
  );

  const loadAccounts = useCallback(async () => {
    if (!institutionId) return;

    try {
      setAccountsLoading(true);
      setAccountsError("");

      const data = await getAccountsForAdmin(institutionId);
      setBankAccounts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch bank accounts:", err);
      setAccountsError(
        err?.message || "Банкны дансны мэдээллийг ачаалж чадсангүй."
      );
      setBankAccounts([]);
    } finally {
      setAccountsLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    async function fetchInstitutionData() {
      try {
        setInstitutionLoading(true);
        setInstitutionError("");

        const data = await getInstitution(institutionId);
        setInstitution(data || null);
      } catch (err) {
        console.error("Failed to fetch institution:", err);
        setInstitutionError(
          err?.message || "Байгууллагын мэдээллийг ачаалж чадсангүй."
        );
        setInstitution(null);
      } finally {
        setInstitutionLoading(false);
      }
    }

    if (institutionId) {
      fetchInstitutionData();
      loadAccounts();
    }
  }, [institutionId, loadAccounts]);

  useEffect(() => {
    const params = {};

    if (searchText) params.search = searchText;
    if (selectedBanks.length > 0) params.banks = selectedBanks.join(",");
    if (selectedCurrencies.length > 0) {
      params.currencies = selectedCurrencies.join(",");
    }
    if (selectedStatuses.length > 0) {
      params.statuses = selectedStatuses.join(",");
    }

    setSearchParams(params);
  }, [
    searchText,
    selectedBanks,
    selectedCurrencies,
    selectedStatuses,
    setSearchParams,
  ]);

  function normalizeText(value) {
    return String(value || "")
      .toLowerCase()
      .replace(/\s+/g, "")
      .replace(/[^a-z0-9]/g, "");
  }

  const bankOptions = useMemo(() => {
    const values = [
      ...new Set(bankAccounts.map((item) => item.bank).filter(Boolean)),
    ];

    return values.sort().map((value) => ({
      label: value,
      value,
    }));
  }, [bankAccounts]);

  const currencyOptions = useMemo(() => {
    const values = [
      ...new Set(bankAccounts.map((item) => item.currency).filter(Boolean)),
    ];

    return values.sort().map((value) => ({
      label: value,
      value,
    }));
  }, [bankAccounts]);

  const filteredBankAccounts = useMemo(() => {
    const keyword = searchText.trim().toLowerCase();
    const normalizedKeyword = normalizeText(searchText);

    return [...bankAccounts]
      .filter((item) => {
        const bankText = String(item.bank || "");
        const currencyText = String(item.currency || "");
        const accountNumberText = String(item.accountNumber || "");
        const isActive = Boolean(item?.is_active);

        const matchesSearch =
          !keyword ||
          bankText.toLowerCase().includes(keyword) ||
          currencyText.toLowerCase().includes(keyword) ||
          accountNumberText.toLowerCase().includes(keyword) ||
          normalizeText(accountNumberText).includes(normalizedKeyword);

        const matchesBank =
          selectedBanks.length === 0 || selectedBanks.includes(item.bank);

        const matchesCurrency =
          selectedCurrencies.length === 0 ||
          selectedCurrencies.includes(item.currency);

        const matchesStatus =
          selectedStatuses.length === 0 ||
          selectedStatuses.includes(isActive ? "ACTIVE" : "INACTIVE");

        return matchesSearch && matchesBank && matchesCurrency && matchesStatus;
      })
      .sort((a, b) => {
        const aActive = Boolean(a?.is_active);
        const bActive = Boolean(b?.is_active);

        if (aActive !== bActive) {
          return aActive ? -1 : 1;
        }

        const bankCompare = String(a?.bank || "").localeCompare(
          String(b?.bank || "")
        );

        if (bankCompare !== 0) return bankCompare;

        return String(a?.accountNumber || "").localeCompare(
          String(b?.accountNumber || "")
        );
      });
  }, [
    bankAccounts,
    searchText,
    selectedBanks,
    selectedCurrencies,
    selectedStatuses,
  ]);

  function handleResetFilters() {
    setSearchText("");
    setSelectedBanks([]);
    setSelectedCurrencies([]);
    setSelectedStatuses([]);
  }

  function handleRemoveSearch() {
    setSearchText("");
  }

  function handleRemoveBank(value) {
    setSelectedBanks((prev) => prev.filter((item) => item !== value));
  }

  function handleRemoveCurrency(value) {
    setSelectedCurrencies((prev) => prev.filter((item) => item !== value));
  }

  function handleRemoveStatus(value) {
    setSelectedStatuses((prev) => prev.filter((item) => item !== value));
  }

  if (institutionLoading) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-8">
        <div className="mx-auto flex max-w-7xl justify-center py-20">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (institutionError) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-8">
        <div className="mx-auto max-w-7xl">
          <Alert type="error" message={institutionError} showIcon />
        </div>
      </div>
    );
  }

  if (!institution) {
    return (
      <div className="min-h-screen bg-slate-100 px-6 py-8">
        <div className="mx-auto max-w-7xl">
          <Card className="rounded-2xl shadow-md">
            <Empty description="Байгууллага олдсонгүй" />
          </Card>
        </div>
      </div>
    );
  }

  const institutionName = institution.name || "Байгууллага";

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-8">
      <div className="mx-auto">
        <div className="mb-6">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/manage/institutions")}
            className="mb-4"
          >
            Буцах
          </Button>

          <Title level={2} className="!mb-1">
            {institutionName}
          </Title>

          <Tag color={getTypeColor(institution?.type || "UNKNOWN")}>
            {institution?.type || "UNKNOWN"}
          </Tag>
        </div>

        <Card className="mb-6 rounded-2xl shadow-md">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
            <div>
              <Text className="mb-2 block font-medium">Данс хайх</Text>
              <Input
                placeholder="Банк, данс, валют хайх"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </div>

            <div>
              <Text className="mb-2 block font-medium">Банк</Text>
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Банкаар шүүх"
                value={selectedBanks}
                options={bankOptions}
                onChange={setSelectedBanks}
              />
            </div>

            <div>
              <Text className="mb-2 block font-medium">Валют</Text>
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Валютаар шүүх"
                value={selectedCurrencies}
                options={currencyOptions}
                onChange={setSelectedCurrencies}
              />
            </div>

            <div>
              <Text className="mb-2 block font-medium">Төлөв</Text>
              <Select
                mode="multiple"
                allowClear
                className="w-full"
                placeholder="Төлвөөр шүүх"
                value={selectedStatuses}
                options={STATUS_OPTIONS}
                onChange={setSelectedStatuses}
              />
            </div>

            <div className="flex items-end">
              <Button onClick={handleResetFilters}>Цэвэрлэх</Button>
            </div>
          </div>
        </Card>

        {(searchText ||
          selectedBanks.length > 0 ||
          selectedCurrencies.length > 0 ||
          selectedStatuses.length > 0) && (
          <Card className="mb-6 rounded-2xl shadow-sm">
            <div className="flex flex-wrap items-center gap-2">
              {searchText ? (
                <Tag closable onClose={handleRemoveSearch}>
                  Хайлт: {searchText}
                </Tag>
              ) : null}

              {selectedBanks.map((bank) => (
                <Tag key={bank} closable onClose={() => handleRemoveBank(bank)}>
                  Банк: {bank}
                </Tag>
              ))}

              {selectedCurrencies.map((currency) => (
                <Tag
                  key={currency}
                  closable
                  onClose={() => handleRemoveCurrency(currency)}
                >
                  Валют: {currency}
                </Tag>
              ))}

              {selectedStatuses.map((status) => (
                <Tag
                  key={status}
                  closable
                  onClose={() => handleRemoveStatus(status)}
                >
                  Төлөв: {status === "ACTIVE" ? "Идэвхтэй" : "Идэвхгүй"}
                </Tag>
              ))}

              <Button type="link" onClick={handleResetFilters} className="!px-0">
                Бүгдийг цэвэрлэх
              </Button>
            </div>
          </Card>
        )}

        {accountsError ? (
          <Card className="mb-6 rounded-2xl shadow-md">
            <Alert type="error" message={accountsError} showIcon />
          </Card>
        ) : null}

        <InstitutionAccountsTable
          title="Банк бусын данснууд"
          loading={accountsLoading}
          accounts={filteredBankAccounts}
          fetchData={loadAccounts}
          columns={headInstitutionAccountColumns({})}
        />
      </div>
    </div>
  );
}

export default HeadInstitutionAccountsPage;